import {
  Button,
  Flex,
  Text,
  Menu,
  MenuButton,
  MenuList,
  Divider,
  useMediaQuery,
} from '@chakra-ui/react';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ChevronDownIcon } from '@chakra-ui/icons';

import MultiSelectCheckBox from 'components/MultiSelectCheckBox';
import {
  PlanToolbarChevronDownIcon,
  PlanToolbarFilterIcon,
} from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/icons';
import { PLAN_TEXT } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planPageRevampUi';
import { usePlanPageRevampVariant } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/usePlanPageUiRevamp';
import {
  FetchBlockItemDimensions,
  getBlockDimensions,
  getIndicatorTableData,
  handleFilters,
  resetSelectedBlockDimension,
  getAppliedFilters,
  getBlockDetails,
  getSelectedBlockDimension,
} from 'redux/PlanPageSlice';
import { getBaseScenario as planPageBaseScenario } from 'redux/ScenarioSlice';
import { indicatorfiltersName } from 'utils/GlobalHelpers';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import store from 'redux/store/store';
import { refetchPlanPageFromStore } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planPageFetchHelpers';
import { persistActiveDimFilterSubsets } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planPageFilterHydration';
import {
  clearPlanFiltersFromIndicatorInputPanel,
  syncPlanFiltersToIndicatorInputPanel,
} from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planPageInputPanelFilters';

import ErrorBoundary from '../../../../components/ErrorBoundary';

/**
 * MultipleIndicatorFilters Component
 *
 * This component includes error handling for:
 * 1. Undefined indicatorTableData or column_headers
 * 2. Safe access to nested properties with try-catch
 * 3. ErrorBoundary wrapper for React error catching
 * 4. Early return with loading state when data is not available
 */
interface MultipleIndicatorFiltersProperties {
  variant?: 'legacy' | 'revamp';
  embedded?: boolean;
  badgeColor?: string;
  onApplyFilters?: () => void;
}

const MultipleIndicatorFilters = ({
  variant = 'legacy',
  embedded = false,
  badgeColor,
  onApplyFilters,
}: MultipleIndicatorFiltersProperties) => {
  const { blockId } = useParams();
  const dispatch: any = useDispatch();

  const [clearButton, setClearButton] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const indicatorTableData = useSelector(getIndicatorTableData);
  const blockDimensions = useSelector(getBlockDimensions);
  const selectedBlockDimension = useSelector(getSelectedBlockDimension);
  const baseScenario = useSelector(planPageBaseScenario);
  const appliedFilters = useSelector(getAppliedFilters);
  const blockDetails = useSelector(getBlockDetails);
  const themeColors = useSelector(getPlanPageThemeColors);
  const isRevamp = usePlanPageRevampVariant(variant);

  const activeFilterCount = useMemo(() => {
    const source =
      appliedFilters && appliedFilters.length > 0 ? appliedFilters : selectedBlockDimension;
    if (!Array.isArray(source)) return 0;
    return source.reduce((count: number, item: any) => {
      const values = Object.values(item)[0];
      return count + (Array.isArray(values) ? values.length : 0);
    }, 0);
  }, [appliedFilters, selectedBlockDimension]);

  // Prefer appliedFilters (synced from localStorage) for chip labels in the filter bar
  const filtersForDisplay =
    appliedFilters && appliedFilters.length > 0 ? appliedFilters : selectedBlockDimension;

  /** Resolves checkbox selection for one dimension from Redux (defaults to all items). */
  const getSelectedItemsForDimension = useCallback(
    (dimensionName: string, allItems: string[]) => {
      const entry = selectedBlockDimension?.find(
        (dimension: Record<string, string[]>) => dimensionName in dimension,
      );
      if (!entry) return allItems;
      const selected = entry[dimensionName];
      if (!Array.isArray(selected) || selected.length === 0) return [];
      return selected;
    },
    [selectedBlockDimension],
  );

  const [isMobile] = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    dispatch(
      FetchBlockItemDimensions({
        blockId,
        parameters: {
          params: { scenario_id: baseScenario?.id },
        },
      }),
    );
  }, []);

  const fetchOutputData = () => {
    refetchPlanPageFromStore(dispatch, store.getState, blockId, blockDetails?.model_id);
  };

  useEffect(() => {
    if (clearButton) {
      if (onApplyFilters) {
        onApplyFilters();
      } else {
        fetchOutputData();
      }
    }
  }, [clearButton]);

  useEffect(() => {
    if (!indicatorTableData?.dimensions?.length) return;
    syncPlanFiltersToIndicatorInputPanel(dispatch, {
      appliedFilters,
      blockDimensions,
      indicatorDimensions: indicatorTableData.dimensions,
    });
  }, [appliedFilters, blockDimensions, dispatch, indicatorTableData?.dimensions]);

  const handleApplyFilter = () => {
    dispatch(handleFilters());
    setIsMenuOpen(false);

    // Save filter to localStorage
    if (blockDetails?.model_id && blockDetails?.id) {
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        const data: Record<string, any> = planFilters ? JSON.parse(planFilters) : {};
        const modelId = String(blockDetails.model_id);
        const currentBlockId = String(blockDetails.id);
        const modelData: Record<string, any> = (data as any)[modelId] || {};
        const blockFilters: any = modelData[currentBlockId] || {};
        const dimensionMeta =
          blockDimensions?.dimensions?.map((dimension: { name: string; items?: string[] }) => ({
            name: dimension.name,
            items: dimension.items ?? [],
          })) ?? [];
        blockFilters.selectedBlockDimension = persistActiveDimFilterSubsets(
          selectedBlockDimension,
          dimensionMeta,
        );
        modelData[currentBlockId] = blockFilters;
        (data as any)[modelId] = modelData;
        localStorage.setItem('global-modal-filters', JSON.stringify(data));
      } catch {
        // Ignore localStorage write failures
      }
    }

    if (onApplyFilters) {
      onApplyFilters();
    } else {
      fetchOutputData();
    }

    if (indicatorTableData?.dimensions?.length) {
      syncPlanFiltersToIndicatorInputPanel(dispatch, {
        appliedFilters: selectedBlockDimension,
        blockDimensions,
        indicatorDimensions: indicatorTableData.dimensions,
      });
    }
  };

  const handleClearAll = async () => {
    setIsMenuOpen(false);
    setClearButton(true);
    await dispatch(resetSelectedBlockDimension());

    // Clear filter from localStorage
    if (blockDetails?.model_id && blockDetails?.id) {
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        if (planFilters) {
          const data: Record<string, any> = JSON.parse(planFilters);
          const modelId = String(blockDetails.model_id);
          const currentBlockId = String(blockDetails.id);
          const modelData: Record<string, any> = (data as any)[modelId] || {};
          if (modelData[currentBlockId]) {
            delete modelData[currentBlockId].selectedBlockDimension;
            (data as any)[modelId] = modelData;
            localStorage.setItem('global-modal-filters', JSON.stringify(data));
          }
        }
      } catch {
        // Ignore localStorage clear failures
      }
    }

    setClearButton(false);
    clearPlanFiltersFromIndicatorInputPanel(dispatch);
    if (onApplyFilters) {
      onApplyFilters();
    }
  };

  return (
    <ErrorBoundary>
      <Menu
        variant={'filter'}
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        onOpen={() => setIsMenuOpen(true)}
        placement='bottom-end'
        strategy='fixed'
      >
        <MenuButton
          as={Button}
          paddingRight={embedded ? undefined : '2'}
          paddingLeft={embedded ? undefined : '2'}
          px={embedded ? 0 : undefined}
          height={isMobile ? '25px' : isRevamp && embedded ? '32px' : isRevamp ? '36px' : '30px'}
          width='fit-content'
          textTransform='none'
          fontWeight={isRevamp ? '500' : '400'}
          fontSize={isMobile ? 'xs' : isRevamp ? '14px' : 'sm'}
          color={isRevamp ? themeColors.textDark : 'white'}
          border={isRevamp && !embedded ? '1px solid' : 'none'}
          borderColor={isRevamp && !embedded ? themeColors.border : undefined}
          borderRadius={isRevamp && !embedded ? '10px' : embedded ? '0' : undefined}
          bg={isRevamp ? 'transparent' : 'transaparant'}
          boxShadow='none'
          display={embedded ? 'inline-flex' : undefined}
          alignItems={embedded ? 'center' : undefined}
          gap={embedded ? 3 : undefined}
          lineHeight={embedded ? '1' : undefined}
          _hover={{
            bg: isRevamp ? themeColors.activeBg : undefined,
            borderColor: isRevamp && !embedded ? themeColors.primary : undefined,
          }}
          _expanded={embedded ? { bg: themeColors.activeBg } : undefined}
          _focus={embedded ? { boxShadow: 'none' } : undefined}
        >
          <Flex alignItems='center' gap={embedded ? 3 : undefined}>
            {embedded && (
              <PlanToolbarFilterIcon width={16} height={16} color={themeColors.primary} />
            )}

            <Text
              {...(isRevamp ? PLAN_TEXT.body : {})}
              size={isRevamp ? undefined : 'Roboto-12'}
              fontSize={isRevamp ? undefined : undefined}
              color={isRevamp ? themeColors.textDark : 'rgba(255, 255, 255, 0.6)'}
              mr={embedded ? 0 : 2}
              whiteSpace='nowrap'
              lineHeight={embedded ? '1.2' : undefined}
            >
              {isRevamp ? 'Filter by' : 'Filter by:'}
            </Text>

            {!embedded &&
              filtersForDisplay &&
              blockDimensions?.dimensions &&
              indicatorfiltersName(filtersForDisplay, blockDimensions.dimensions)?.map(
                (item: any, index: number) => (
                  <React.Fragment key={index}>
                    {item && (
                      <>
                        <Divider
                          mx={2}
                          orientation='vertical'
                          size='2xs'
                          colorScheme='grey'
                          height='12px'
                          alignSelf='center'
                        />
                        <Text
                          {...(isRevamp ? PLAN_TEXT.bodySemibold : {})}
                          size={isRevamp ? undefined : 'Roboto-12'}
                          fontSize={isRevamp ? undefined : undefined}
                          fontWeight={isRevamp ? undefined : undefined}
                          color={isRevamp ? themeColors.primary : undefined}
                          whiteSpace={'nowrap'}
                        >
                          {item}
                        </Text>
                      </>
                    )}
                  </React.Fragment>
                ),
              )}

            {isRevamp && activeFilterCount > 0 && (
              <Flex
                minW='18px'
                h='18px'
                px={1}
                ml={embedded ? 0 : 2}
                borderRadius='full'
                bg={badgeColor ?? themeColors.danger}
                color={themeColors.white}
                {...(isRevamp ? PLAN_TEXT.badge : {})}
                alignItems='center'
                justifyContent='center'
                flexShrink={0}
                fontSize={isRevamp ? undefined : '10px'}
              >
                {activeFilterCount}
              </Flex>
            )}

            {embedded ? (
              <PlanToolbarChevronDownIcon width={10} height={6} color={themeColors.primary} />
            ) : (
              <ChevronDownIcon
                boxSize={isRevamp ? 3 : undefined}
                color={isRevamp ? themeColors.primary : undefined}
              />
            )}
          </Flex>
        </MenuButton>
        <MenuList zIndex={1500} minW={isRevamp ? '320px' : undefined}>
          <Flex
            gap={8}
            maxW={isMobile ? '300px' : '600px'}
            overflowX={'auto'}
            className='sm-scroll'
            pb={4}
          >
            {blockDimensions?.dimensions?.map((checkBoxItem: any, index: number) => (
              <React.Fragment key={index}>
                <MultiSelectCheckBox
                  clearButton={clearButton}
                  checkboxData={checkBoxItem}
                  isMenuOpen={isMenuOpen}
                  selectedItems={getSelectedItemsForDimension(
                    checkBoxItem.name,
                    checkBoxItem.items ?? [],
                  )}
                />
              </React.Fragment>
            ))}
          </Flex>
          <Flex justifyContent={'space-between'} mt={4}>
            {blockDimensions?.dimensions?.length > 1 && (
              <Button variant={'clear_button'} onClick={handleClearAll}>
                Clear all
              </Button>
            )}
            <Flex>
              <Button variant={'cancel_button'} mr={3} onClick={() => setIsMenuOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleApplyFilter} variant={'save_button'}>
                Apply
              </Button>
            </Flex>
          </Flex>
        </MenuList>
      </Menu>
    </ErrorBoundary>
  );
};

export default MultipleIndicatorFilters;
